import { useState, type FormEvent } from 'react';
import { ApiError } from '../api/client';
import {
  useCreateSendWindow,
  useDeleteSendWindow,
  useSendWindows,
  useUpdateSendWindow,
} from '../api/queries';
import type { SendWindow } from '../api/types';
import { SendWindowsWeekCalendar } from './SendWindowsWeekCalendar';
import { useToast } from './Toast';

/** YYYY-MM-DD in lokaler Zeit -- toISOString() würde auf UTC umrechnen und dabei den Tag verschieben. */
function toDateInput(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function toTimeInput(date: Date): string {
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
}

/**
 * Baut aus Datum + Uhrzeiten zwei absolute Zeitpunkte in lokaler Zeitzone.
 * Liegt die Endzeit nicht nach der Startzeit, gilt sie als "am nächsten
 * Tag" -- so lässt sich ein über Mitternacht laufendes Fenster eingeben,
 * ohne zwei Tage wählen zu müssen.
 */
function buildRange(date: string, startTime: string, endTime: string): { startAt: string; endAt: string } {
  const [year, month, day] = date.split('-').map(Number) as [number, number, number];
  const [startHour, startMinute] = startTime.split(':').map(Number) as [number, number];
  const [endHour, endMinute] = endTime.split(':').map(Number) as [number, number];

  const start = new Date(year, month - 1, day, startHour, startMinute);
  const end = new Date(year, month - 1, day, endHour, endMinute);
  if (end.getTime() <= start.getTime()) {
    end.setDate(end.getDate() + 1);
  }

  return { startAt: start.toISOString(), endAt: end.toISOString() };
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('de-DE', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

function sameDay(a: string, b: string): boolean {
  const dateA = new Date(a);
  const dateB = new Date(b);
  return (
    dateA.getFullYear() === dateB.getFullYear() &&
    dateA.getMonth() === dateB.getMonth() &&
    dateA.getDate() === dateB.getDate()
  );
}

interface FormState {
  date: string;
  startTime: string;
  endTime: string;
}

function emptyForm(): FormState {
  return { date: toDateInput(new Date()), startTime: '18:00', endTime: '19:00' };
}

function formFromWindow(sendWindow: SendWindow): FormState {
  return {
    date: toDateInput(new Date(sendWindow.startAt)),
    startTime: toTimeInput(new Date(sendWindow.startAt)),
    endTime: toTimeInput(new Date(sendWindow.endAt)),
  };
}

/**
 * Abschickzeiten: Zeitfenster, in denen das Konto einen Angriffsplan
 * abschicken könnte. Rein persönliche Angabe am eigenen Profil -- nur der
 * jeweilige Nutzer sieht und verwaltet seine eigenen (eine gemeinsame
 * Planungsansicht für den Stamm wäre ein eigenes, späteres Feature).
 */
export function SendWindowsSection() {
  const sendWindows = useSendWindows();
  const create = useCreateSendWindow();
  const update = useUpdateSendWindow();
  const remove = useDeleteSendWindow();
  const toast = useToast();

  const [form, setForm] = useState<FormState>(emptyForm());
  const [editingId, setEditingId] = useState<number | null>(null);

  const busy = create.isPending || update.isPending;
  const error = create.error ?? update.error;

  const rows = [...(sendWindows.data?.windows ?? [])].sort((a, b) =>
    a.startAt.localeCompare(b.startAt),
  );

  const startEdit = (sendWindow: SendWindow) => {
    setEditingId(sendWindow.id);
    setForm(formFromWindow(sendWindow));
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(emptyForm());
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    const range = buildRange(form.date, form.startTime, form.endTime);

    if (editingId !== null) {
      update.mutate(
        { id: editingId, ...range },
        {
          onSuccess: () => {
            toast.push('success', 'Abschickzeit wurde gespeichert.');
            cancelEdit();
          },
          onError: (updateError) => toast.push('error', updateError.message),
        },
      );
    } else {
      create.mutate(range, {
        onSuccess: () => {
          toast.push('success', 'Abschickzeit wurde angelegt.');
          setForm(emptyForm());
        },
        onError: (createError) => toast.push('error', createError.message),
      });
    }
  };

  const confirmDelete = (sendWindow: SendWindow) => {
    if (!window.confirm('Diese Abschickzeit löschen?')) return;
    remove.mutate(
      { id: sendWindow.id },
      {
        onSuccess: () => toast.push('success', 'Abschickzeit wurde gelöscht.'),
        onError: (removeError) => toast.push('error', removeError.message),
      },
    );
  };

  return (
    <section className="card">
      <h2>Abschickzeiten</h2>
      <p className="muted">
        Zeitfenster, in denen du einen Angriffsplan abschicken könntest — nur für dich sichtbar.
      </p>

      {error instanceof ApiError && <div className="flash flash-error">{error.message}</div>}

      <form className="form" onSubmit={handleSubmit} noValidate>
        <div className="field-row">
          <label className="field">
            <span>Datum</span>
            <input
              type="date"
              value={form.date}
              onChange={(event) => setForm((current) => ({ ...current, date: event.target.value }))}
              required
            />
          </label>
          <label className="field">
            <span>Von</span>
            <input
              type="time"
              value={form.startTime}
              onChange={(event) =>
                setForm((current) => ({ ...current, startTime: event.target.value }))
              }
              required
            />
          </label>
          <label className="field">
            <span>Bis</span>
            <input
              type="time"
              value={form.endTime}
              onChange={(event) => setForm((current) => ({ ...current, endTime: event.target.value }))}
              required
            />
          </label>
        </div>

        <div className="button-row">
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? 'Wird gespeichert …' : editingId !== null ? 'Speichern' : 'Hinzufügen'}
          </button>
          {editingId !== null && (
            <button type="button" className="btn btn-ghost" onClick={cancelEdit}>
              Abbrechen
            </button>
          )}
        </div>
      </form>

      {!sendWindows.isPending && (
        <>
          <h3>Diese Woche</h3>
          <p className="muted" style={{ marginTop: '-4px' }}>
            Ziehen, um eine neue Abschickzeit anzulegen — auf einen Block klicken, um ihn zu löschen.
          </p>
          <SendWindowsWeekCalendar windows={rows} />
        </>
      )}

      {sendWindows.isPending && <p className="muted">Wird geladen …</p>}

      {!sendWindows.isPending && rows.length === 0 && (
        <p className="muted center pad">Noch keine Abschickzeiten hinterlegt.</p>
      )}

      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Datum</th>
                <th>Von</th>
                <th>Bis</th>
                <th className="right">Aktion</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((sendWindow) => (
                <tr key={sendWindow.id}>
                  <td className="nowrap">{formatDate(sendWindow.startAt)}</td>
                  <td className="nowrap">{formatTime(sendWindow.startAt)}</td>
                  <td className="nowrap">
                    {formatTime(sendWindow.endAt)}
                    {!sameDay(sendWindow.startAt, sendWindow.endAt) && ' (+1 Tag)'}
                  </td>
                  <td className="right">
                    <div className="actions">
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={() => startEdit(sendWindow)}
                      >
                        Bearbeiten
                      </button>
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        disabled={remove.isPending}
                        onClick={() => confirmDelete(sendWindow)}
                      >
                        Löschen
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
